import { TextInput, TextInputProps } from 'components/forms/TextInput';
import { forwardRef, Ref } from 'react';

type NumberInputProps = Omit<TextInputProps, 'type' | 'inputMode'>;

// This regex is designed to allow all any partially-complete valid numbers
// (assuming one is typing in the characters from left to right). It attempts to
// reject any string that can never represet a valid number assuming appending
// chars is the only available operation. (i.e. if a char must be removed or
// prepended for it to become a valid number, the string should not match.)
export const NumberInputRegex = /^(?:[+-]?[0-9]*[.,]?[0-9]*?)(?:[0-9]+e[+-]?[0-9]*)?$/;

// https://stackoverflow.blog/2022/12/26/why-the-number-input-is-the-worst-input/

// input type="text" because:
// - mapping a blank input -- ""  to undefined, not 0 becomes impossible without
//   losing 0
// - performing validation on the entered string becomes impossible.
function NumberInput(
    { required, onBeforeInput, ...rest }: NumberInputProps,
    ref: Ref<HTMLInputElement>,
) {
    return (
        <TextInput
            {...rest}
            ref={ref}
            type="text"
            inputMode="numeric"
            required={required}
            onBeforeInput={(e) => {
                onBeforeInput?.(e);
                const existingValue = (e.target as HTMLInputElement).value;
                const newValue = `${existingValue}${e.nativeEvent.data}`;

                if (!NumberInputRegex.test(newValue)) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }}
        />
    );
}

const forwardRefNumberInput = forwardRef(NumberInput);

export { forwardRefNumberInput as NumberInput };
